/**
 * Tipos e utilitários para planos VOVER (capacidade de condomínios).
 */

export interface VoverPlanPack {
  capacity: number;
  price: number;
  label?: string;
  icon?: string;
  features?: string[];
  order?: number;
  highlight?: string;
}

export type RawCapacityPack = {
  capacity: number;
  price: number;
  label?: string;
  icon?: string;
  features?: string[];
  order?: number;
  highlight?: string;
};

/** Normaliza packs vindos da API (clientCapacityPacks ou condoCapacityPacks legado). */
export function normalizePlanPacks(
  packs: RawCapacityPack[] | undefined | null
): VoverPlanPack[] {
  if (!packs?.length) return [];
  return [...packs]
    .map((p, index) => {
      const pack: VoverPlanPack = {
        capacity: p.capacity,
        price: p.price,
        features: Array.isArray(p.features) ? p.features.filter(Boolean) : [],
        order: p.order ?? index,
      };
      const label = p.label?.trim();
      if (label) pack.label = label;
      if (p.icon) pack.icon = p.icon;
      const highlight = p.highlight?.trim();
      if (highlight) pack.highlight = highlight;
      return pack;
    })
    .sort((a, b) => (a.order ?? a.capacity) - (b.order ?? b.capacity));
}

export function getGestaoPacksFromPricing(pricingConfig: {
  availablePackages?: Array<{
    key?: string;
    clientCapacityPacks?: RawCapacityPack[];
    condoCapacityPacks?: RawCapacityPack[];
  }>;
  packages?: {
    gestao?: { clientCapacityPacks?: RawCapacityPack[]; condoCapacityPacks?: RawCapacityPack[] };
    operador?: { clientCapacityPacks?: RawCapacityPack[]; condoCapacityPacks?: RawCapacityPack[] };
  };
} | null | undefined): VoverPlanPack[] {
  if (!pricingConfig) return [];
  const ap =
    pricingConfig.availablePackages?.find(
      (p) => ['gestao', 'operador'].includes((p.key || '').toLowerCase())
    ) ??
    pricingConfig.availablePackages?.[0];
  const fromAp = ap?.clientCapacityPacks ?? ap?.condoCapacityPacks;
  if (fromAp?.length) return normalizePlanPacks(fromAp);
  const gestao = pricingConfig.packages?.gestao ?? pricingConfig.packages?.operador;
  return normalizePlanPacks(gestao?.clientCapacityPacks ?? gestao?.condoCapacityPacks);
}

export type BillingPackageType = 'condominio' | 'gestao' | 'institucional';

/** Pacote efetivo do tenant (API billing ou activePackage). */
export function resolveBillingPackageType(
  billing: {
    packageType?: string | null;
    activePackage?: { packageType?: string | null };
  } | null | undefined,
  pricingConfig?: {
    availablePackages?: Array<{ key?: string; enabled?: boolean }>;
  } | null
): BillingPackageType {
  const raw = billing?.packageType ?? billing?.activePackage?.packageType;
  if (raw === 'gestao' || raw === 'condominio' || raw === 'institucional') {
    return raw;
  }
  const pkgs = (pricingConfig?.availablePackages ?? []).filter(
    (p) => p.enabled !== false
  );
  const keys = pkgs.map((p) => (p.key || '').toLowerCase());
  if (keys.includes('gestao') || keys.includes('operador')) return 'gestao';
  if (keys.length === 1 && keys[0] === 'institucional') return 'institucional';
  if (keys.length === 1 && keys[0] === 'condominio') return 'condominio';
  if (keys.includes('gestao')) return 'gestao';
  return 'gestao';
}

export function getMinPlanCapacity(packs: VoverPlanPack[]): number {
  if (!packs.length) return 10;
  return Math.min(...packs.map((p) => p.capacity));
}

export function getPlanDisplayName(pack: VoverPlanPack): string {
  return pack.label?.trim() || `Até ${pack.capacity} condomínios`;
}

export interface PlanComparisonRow {
  feature: string;
  /** Por plano: true = incluso, false = não incluso */
  includedByPlan: boolean[];
}

/** Monta linhas da tabela comparativa preservando ordem das funcionalidades. */
export function buildPlanComparisonRows(packs: VoverPlanPack[]): PlanComparisonRow[] {
  const orderedFeatures: string[] = [];
  const seen = new Set<string>();
  for (const pack of packs) {
    for (const f of pack.features ?? []) {
      const trimmed = f.trim();
      if (trimmed && !seen.has(trimmed)) {
        seen.add(trimmed);
        orderedFeatures.push(trimmed);
      }
    }
  }
  return orderedFeatures.map((feature) => ({
    feature,
    includedByPlan: packs.map((p) => (p.features ?? []).some((x) => x.trim() === feature)),
  }));
}

export function formatPlanCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

/** Preço mensal para capacidade exata ou customizada (proporcional ao tier inferior). */
export function calculateGestaoCapacityPrice(count: number, packs: VoverPlanPack[]): number {
  if (!packs.length || count <= 0) return 0;
  const sorted = [...packs].sort((a, b) => a.capacity - b.capacity);
  const exact = sorted.find((p) => p.capacity === count);
  if (exact) return exact.price;
  const packBelow = sorted.filter((p) => p.capacity < count).pop();
  const base = packBelow ?? sorted[0];
  if (!base) return 0;
  return Math.round(count * (base.price / base.capacity) * 100) / 100;
}
